/**
 *
 * Dentist Overview
 *
 */

import { useCallback, useEffect, useState } from "react";
import * as PropTypes from "prop-types";
import { FormattedMessage, injectIntl } from "react-intl";
import History from "utils/history";
import GlobalMessages from "messages/messages";
import Search from "basicComponents/Searchv2/Searchv2";
import { ICONS } from "basicComponents/Searchv2/Icons";
import ConfirmModal from "../../basicComponents/ConfirmModal/ConfirmModal";
import messages from "../../containers/DentistDataPage/DentistData/messages";
import styled from "styled-components";
import InfoModal from "../../basicComponents/InfoModal/InfoModal";
import { useDebounce } from "use-debounce";

function DentistOverview({
  intl,
  practice,
  initialInputText = "",
  showArrow = true,
  items,
  maxHeight = false,
  onChange,
  onFilter,
  failedDeleteMessage,
  value,
  createDentist,
  deleteDentist,
  height,
  dentistDeleted,
}) {
  const [inputText, setInputText] = useState(initialInputText);
  const [dentistItem, setDentistItem] = useState({});
  const [showDentistDeleteModal, setDentistDeleteModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(true);

  const [debouncedFilter] = useDebounce(inputText, 300);

  useEffect(
    () => {
      onFilter({ filter: debouncedFilter }, practice.id);
    },
    [debouncedFilter, onFilter, practice.id],
  );

  const handleChange = useCallback(e => {
    setInputText(e.target.value);
  }, []);

  const handleIconClicked = useCallback(
    (icon, dentist) => {
      if (icon === ICONS.DELETE) {
        handleShowDentistDeleteModal(dentist);
      }
      if (icon === ICONS.SHOW_MORE) {
        History.push({ pathname: `/dentist/${dentist.id}` });
      }
    },
    [handleShowDentistDeleteModal],
  );

  const save = useCallback(
    callback => {
      const dentist = {
        name: value || inputText,
        practiceId: practice.id,
      };

      createDentist(dentist, callback, false);
    },
    [createDentist, inputText, practice.id, value],
  );

  const handleUglyButtonClicked = useCallback(
    () => {
      // after creating a new dentist it gets shown in the list.
      save(() => {
        onFilter(value || { filter: inputText }, practice.id);
      });
    },
    [inputText, onFilter, practice.id, save, value],
  );

  const handleCloseDentistDeleteModal = useCallback(() => {
    setDentistDeleteModal(false);
  }, []);
  const handleShowDentistDeleteModal = useCallback(dentist => {
    setDentistDeleteModal(true);
    setDentistItem(dentist);
  }, []);

  useEffect(
    () => {
      if (failedDeleteMessage === null && dentistDeleted) {
        History.push({ pathname: "/dashboard" });
      } else {
        setShowInfoModal(true);
      }
    },
    [dentistDeleted, failedDeleteMessage],
  );

  const handleDeleteDentist = useCallback(
    () => {
      handleCloseDentistDeleteModal();
      deleteDentist(dentistItem);
    },
    [deleteDentist, dentistItem, handleCloseDentistDeleteModal],
  );

  const handleArrowClicked = useCallback(() => {
    History.push({ pathname: "/dentists" });
  }, []);

  const handleCloseInfoModal = useCallback(() => {
    setShowInfoModal(false);
  }, []);

  return (
    <OutContainer>
      <Container>
        <ConfirmModal
          open={showDentistDeleteModal}
          onCancel={handleCloseDentistDeleteModal}
          onConfirm={handleDeleteDentist}
        >
          <FormattedMessage {...messages.deleteDentist} />
        </ConfirmModal>

        {failedDeleteMessage && (
          <InfoModal
            onConfirm={handleCloseInfoModal}
            open={showInfoModal}
            header="Unable to delete dentist"
          >
            {failedDeleteMessage.message}
          </InfoModal>
        )}

        <Search
          noResultButtonId="addNewDentistButton"
          id="dentistOverviewSearch"
          items={items}
          placeholder={GlobalMessages.search_message_dentist}
          icons={["delete", "show"]}
          enableUglyLongButtonOnNoResult
          handleUglyButtonClicked={handleUglyButtonClicked}
          height={height}
          maxHeight={maxHeight}
          showArrow={showArrow}
          onIconClicked={handleIconClicked}
          handleArrowClicked={handleArrowClicked}
          arrowId="dentistArrowButton"
          practice={practice}
          showTableContent
          onChange={onChange || handleChange}
          iconButtonText={
            <FormattedMessage {...GlobalMessages.addNewDentist} />
          }
          value={onChange ? value : inputText}
          headers={[
            {
              Header: <FormattedMessage {...GlobalMessages.dentist} />,
              accessor: "name",
            },
          ]}
        />
      </Container>
    </OutContainer>
  );
}

DentistOverview.propTypes = {
  intl: PropTypes.object.isRequired,
  practice: PropTypes.any.isRequired,
  initialInputText: PropTypes.string,
  showArrow: PropTypes.bool,
  items: PropTypes.array.isRequired,
  maxHeight: PropTypes.bool,
  showTableContent: PropTypes.bool,
  onChange: PropTypes.func,
  onFilter: PropTypes.func.isRequired,

  failedDeleteMessage: PropTypes.object,
  patients: PropTypes.array,
  value: PropTypes.string,
  height: PropTypes.number,
};

const Container = styled.div`
  min-width: 100%;
  max-width: 100%;
`;

const OutContainer = styled.div`
  display: flex;
  justify-content: center;
`;

export default injectIntl(DentistOverview);
